import React, { forwardRef } from 'react'

import { InputBaseComponentProps, Box, FormHelperText, useTheme } from '@mui/material'
import { IMaskInput } from 'react-imask'
import { PhoneInput } from 'react-international-phone'
// eslint-disable-next-line import/no-unresolved
import 'react-international-phone/style.css'

const phoneMask = '(#00) 000-0000'

type PhoneNumberMaskedInputProps = InputBaseComponentProps & {
  name: string
  onChange: (event: { target: { name: string; value: string } }) => void
}

export const PhoneNumberMaskedInput = forwardRef<
  HTMLElement,
  PhoneNumberMaskedInputProps
>(function PhoneNumberMasked(props, ref) {
  const { onChange, ...rest } = props

  return (
    <IMaskInput
      {...rest}
      lazy={true}
      mask={phoneMask}
      definitions={{
        '#': /[1-9]/,
      }}
      onAccept={(value) => {
        onChange?.({ target: { name: props.name, value: ((value as string).replace(/\D/g, '')) } });
      }
      }
      inputRef={ref as any}
    />
  )
})

export interface PhoneNumberInputProps {
  value: string
  onChange: (val: string) => void
  error?: boolean
  helperText?: string
  placeholder?: string
  disabled?: boolean
  endAdornment?: React.ReactNode
  context?: 'portal' | 'dashboard' | 'onboarding'
  height?: string
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  error = false,
  helperText = '',
  placeholder = '',
  disabled = false,
  endAdornment,
  context = 'portal',
  height,
}) => {
  const isInitialMount = React.useRef(true)

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      isInitialMount.current = false
    }, 100)
    return () => clearTimeout(timeout)
  }, [])

  const handleChange = (val: string) => {
    if (isInitialMount.current && (!value || value === '') && val.startsWith('+') && val.length <= 4) {
      return
    }
    onChange(val)
  }

  const theme = useTheme()
  
  const bgColor = context === 'onboarding' ? '#1A1B20' : 'transparent'
  const dropdownBgColor = context === 'onboarding' ? '#1A1B20' : '#FFFFFF'
  const textColor = disabled ? 'rgba(0, 0, 0, 0.38)' : (context === 'onboarding' ? '#EFEDFDB2' : '#000000')
  const defaultBorderColor = context === 'dashboard' ? '#9135E0' : (context === 'onboarding' ? '#EFEDFDB2' : 'rgba(0, 0, 0, 0.23)')
  const hoverBorderColor = error ? '#ff3d00' : (disabled ? '#E2E8F0' : (context === 'dashboard' ? '#9135E0' : (context === 'onboarding' ? '#EFEDFDB2' : 'rgba(0, 0, 0, 0.87)')))
  const focusBorderColor = error ? '#ff3d00' : (disabled ? '#E2E8F0' : (context === 'dashboard' ? '#9135E0' : (context === 'onboarding' ? '#EFEDFDB2' : theme.palette.primary.main)))
  const borderColor = error ? '#ff3d00' : (disabled ? '#E2E8F0' : defaultBorderColor)
  const dropdownBorderColor = context === 'onboarding' ? '#EFEDFDB2' : '#EFEDFDB2'
  const scrollbarThumbColor = context === 'onboarding' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
  const scrollbarThumbHoverColor = context === 'onboarding' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
  const itemHoverColor = context === 'onboarding' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
  const itemSelectedColor = context === 'onboarding' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
  const itemSelectedHoverColor = context === 'onboarding' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'

  const inputHeight = height ? height : (context === 'dashboard' ? '50px' : '44px')
  const borderRadius = context === 'dashboard' || context === 'onboarding' ? '10px' : '4px'

  return (
    <Box sx={{
      width: '100%',
      position: 'relative',
      '& .react-international-phone-input-container': {
        position: 'relative !important',
        gap: '0 !important',
      },
      '& .react-international-phone-country-selector': {
        position: 'static !important',
      },
      '& .react-international-phone-country-selector-button': {
        height: `${inputHeight} !important`,
        boxSizing: 'border-box',
        backgroundColor: bgColor,
        border: `1px solid ${borderColor} !important`,
        borderTopLeftRadius: borderRadius,
        borderBottomLeftRadius: borderRadius,
        padding: '0 10px',
        color: textColor,
        opacity: disabled ? 0.5 : 1,
      },
      '& .react-international-phone-input': {
        flex: 1,
        width: '100%', 
        height: `${inputHeight} !important`,
        boxSizing: 'border-box',
        padding: '8.5px 14px',
        fontSize: '14px',
        fontFamily: 'InterRegular',
        backgroundColor: bgColor,
        border: `1px solid ${borderColor} !important`,
        borderLeft: 'none !important',
        borderTopRightRadius: borderRadius,
        borderBottomRightRadius: borderRadius,
        color: textColor,
        opacity: disabled ? 0.5 : 1,
      },
      '&:hover .react-international-phone-country-selector-button': {
        border: `1px solid ${hoverBorderColor} !important`,
      },
      '&:hover .react-international-phone-input': {
        border: `1px solid ${hoverBorderColor} !important`,
        borderLeft: 'none !important',
      },
      '&:focus-within .react-international-phone-country-selector-button': {
        border: `1px solid ${focusBorderColor} !important`,
        ...(context === 'portal' ? { borderWidth: '2px !important', borderRightWidth: '1px !important' } : {})
      },
      '&:focus-within .react-international-phone-input': {
        border: `1px solid ${focusBorderColor} !important`,
        borderLeft: 'none !important',
        ...(context === 'portal' ? { borderWidth: '2px !important', borderLeftWidth: '0 !important', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : {})
      },
      '& .react-international-phone-country-selector-dropdown': {
        width: '100% !important',
        backgroundColor: dropdownBgColor,
        color: textColor,
        marginTop: '4px',
        borderRadius: '4px',
        maxHeight: context === 'dashboard' ? '200px !important' : '300px !important',
        border: `1px solid ${dropdownBorderColor}`,
        outline: 'none',
        zIndex: 1300,
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: dropdownBgColor,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: dropdownBgColor,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: scrollbarThumbColor,
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: scrollbarThumbHoverColor,
        },
      },
      '& .react-international-phone-country-selector-dropdown__list-item': {
        fontFamily: 'InterRegular !important',
        fontSize: '14px !important',
        color: `${textColor} !important`,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: `${itemHoverColor} !important`,
        },
        '&[aria-selected="true"]': {
          backgroundColor: `${itemSelectedColor} !important`,
          '&:hover': {
            backgroundColor: `${itemSelectedHoverColor} !important`,
          }
        }
      }
    }}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <PhoneInput
          disabled={disabled}
          value={value}
          onChange={handleChange}
          defaultCountry="us"
          style={{ display: 'flex', width: '100%' }}
          inputProps={{
            placeholder: placeholder,
          }}
          inputStyle={{}}
          countrySelectorStyleProps={{
            buttonStyle: {},
            dropdownStyleProps: {
              style: {
                position: 'absolute',
                top: '100%',
                left: 0,
                width: '100%',
                zIndex: 1300,
                backgroundColor: dropdownBgColor,
                color: textColor,
                border: `1px solid ${dropdownBorderColor}`
              }
            }
          }}
        />
        {endAdornment && (
          <Box sx={{
            position: 'absolute',
            right: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            pointerEvents: disabled ? 'none' : 'auto'
          }}>
            {endAdornment}
          </Box>
        )}
      </Box>
      {helperText && (
        <FormHelperText error={error} sx={{ 
          marginLeft: 0, 
          marginRight: 0,
          fontFamily: 'InterSemiBold',
          fontSize: '14px',
          lineHeight: '17px',
          fontWeight: 400,
          paddingLeft: '14px',
          ...(error ? { color: 'rgb(255, 61, 0) !important' } : {})
        }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  )
}
